import { useEffect, useState } from 'react';
import {
  getPurchaseHospitalItemList,
  type PurchaseHospitalItemRow,
  type PurchasePlanGoodsRow,
} from '@/api/purchase';
import { UNLIMITED_SPEC_LABEL } from '@/pages/purchase-plan/components/UniqueSpecOrderItemsTable';

interface HospitalPlanItemsModalProps {
  isOpen: boolean;
  source: PurchasePlanGoodsRow | null;
  onClose: () => void;
}

function displaySpecName(row: PurchaseHospitalItemRow) {
  if (!(Number(row.spec_id) > 0)) return UNLIMITED_SPEC_LABEL;
  return row.spec_name || row.spec?.name || '-';
}

/** 与订单/采购计划状态标签同色 */
function getFlowStatusTag(status?: number | string, statusText?: string) {
  const n = Number(status);
  const label = statusText || '-';
  if (n === 10) return { color: '#19be6b', label };
  if (n === 1) return { color: '#2d8cf0', label };
  if (n === 2) return { color: '#ed4014', label };
  if (n === 99) return { color: '#c5c8ce', label };
  return { color: '#33bcb7', label };
}

export default function HospitalPlanItemsModal({
  isOpen,
  source,
  onClose,
}: HospitalPlanItemsModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState<PurchaseHospitalItemRow[]>([]);
  const [goodsTitle, setGoodsTitle] = useState('');
  const [specLabel, setSpecLabel] = useState('');
  const [monthLabel, setMonthLabel] = useState('');

  useEffect(() => {
    if (!isOpen || !source) {
      setLoading(false);
      setError('');
      setItems([]);
      setGoodsTitle('');
      setSpecLabel('');
      setMonthLabel('');
      return;
    }

    const goodsId = Number(source.goods_id || source.goods?.id) || 0;
    const flowItemId = Number(source.flow_item_id) || 0;
    const specId = Number(source.spec_id) > 0 ? Number(source.spec_id) : 0;
    if (goodsId < 1 || flowItemId < 1) {
      setError('物资或采购项目无效');
      setItems([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    void (async () => {
      try {
        const res = await getPurchaseHospitalItemList({
          goods_id: goodsId,
          spec_id: specId,
          flow_item_id: flowItemId,
        });
        if (cancelled) return;
        const ok = res.error === false || Number(res.error) === 0;
        if (!ok) {
          setItems([]);
          setError(res.message || res.msg || '加载失败');
          return;
        }
        setItems(Array.isArray(res.data) ? res.data : []);
        setGoodsTitle(
          String(res.goods?.title || source.goods_name || source.goods?.title || '').trim(),
        );
        setSpecLabel(
          String(
            res.goods?.spec_name ||
              (specId > 0 ? source.spec_name || source.spec?.name : UNLIMITED_SPEC_LABEL) ||
              '',
          ).trim(),
        );
        setMonthLabel(String(res.goods?.apply_month_text || '').trim());
      } catch {
        if (!cancelled) {
          setItems([]);
          setError('网络异常，加载失败');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, source]);

  if (!isOpen) return null;

  const totalQty = items.reduce((sum, row) => sum + (Number(row.plan_quantity ?? 0) || 0), 0);

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-background-50 rounded-xl w-full max-w-5xl shadow-2xl max-h-[85vh] flex flex-col">
        <div className="px-5 py-4 border-b border-background-200 flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-foreground-900">全院科室计划明细</h3>
            <p className="text-xs text-foreground-500 mt-1 truncate">
              {[monthLabel, goodsTitle, specLabel].filter(Boolean).join(' · ') || '同物资计划对照'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 h-8 w-8 inline-flex items-center justify-center rounded-lg text-foreground-400 hover:bg-background-100 hover:text-foreground-700"
            title="关闭"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1 min-h-0">
          {loading ? (
            <div className="py-16 text-center text-sm text-foreground-500">
              <i className="ri-loader-4-line animate-spin text-lg mr-1.5" />
              加载中...
            </div>
          ) : error ? (
            <div className="py-12 text-center text-sm text-red-600">
              <i className="ri-error-warning-line mr-1" />
              {error}
            </div>
          ) : items.length === 0 ? (
            <p className="py-12 text-center text-sm text-foreground-400">
              暂无其它科室或全院的同物资计划
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-background-200">
              <table className="w-full text-sm border-collapse [&_th]:border [&_th]:border-background-200 [&_td]:border [&_td]:border-background-100">
                <thead>
                  <tr className="bg-background-50">
                    <th className="py-2 px-2 text-center text-xs font-semibold text-foreground-600 w-10">
                      序号
                    </th>
                    <th className="py-2 px-3 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      计划类型
                    </th>
                    <th className="py-2 px-3 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      科室
                    </th>
                    <th className="py-2 px-3 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      规格
                    </th>
                    <th className="py-2 px-3 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      计划数
                    </th>
                    <th className="py-2 px-3 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap">
                      状态
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row, idx) => (
                    <tr
                      key={String(row.flow_item_id ?? row.id ?? idx)}
                      className="hover:bg-background-50/60"
                    >
                      <td className="py-2 px-2 text-center text-xs text-foreground-500">
                        {idx + 1}
                      </td>
                      <td className="py-2 px-3 text-xs text-foreground-700 whitespace-nowrap">
                        {row.control_type_text ||
                          (Number(row.control_type) === 1
                            ? '全院限购'
                            : Number(row.control_type) === 3
                              ? '个人限购'
                              : '科室限购')}
                      </td>
                      <td className="py-2 px-3 text-xs text-foreground-700 whitespace-nowrap">
                        {Number(row.control_type) === 1
                          ? '全院'
                          : row.department_name || '-'}
                      </td>
                      <td
                        className={`py-2 px-3 text-xs max-w-[280px] truncate ${
                          !(Number(row.spec_id) > 0)
                            ? 'text-accent-700 font-medium'
                            : 'text-foreground-600'
                        }`}
                      >
                        {displaySpecName(row)}
                      </td>
                      <td className="py-2 px-3 text-center text-xs font-medium text-foreground-800 whitespace-nowrap">
                        {Number(row.plan_quantity ?? 0) || 0}
                      </td>
                      <td className="py-2 px-3 text-center whitespace-nowrap">
                        {(() => {
                          const tag = getFlowStatusTag(row.flow_status, row.flow_status_text);
                          return (
                            <span
                              className="inline-flex px-1.5 py-0.5 rounded text-[11px] font-medium text-white"
                              style={{ backgroundColor: tag.color }}
                            >
                              {tag.label}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-background-50">
                    <td
                      colSpan={4}
                      className="py-2 px-3 text-right text-xs font-medium text-foreground-700"
                    >
                      合计
                    </td>
                    <td className="py-2 px-3 text-center text-xs font-bold text-foreground-900">
                      {totalQty}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-background-200 flex justify-end shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-background-300 text-sm text-foreground-700 hover:bg-background-50"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
}
